import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Select from "react-select";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import { useToast } from "../../../Toaster/ToastContext";
import PropTypes from "prop-types";
import globals from "../../../globals";
import { apiClient } from "../../../services/apiClient";
import { setCurrentCustomer } from "../../../core/redux/action";
import { useDispatch } from "react-redux";

const CustomerModal = ({ customer, onOperationComplete, onClose, show }) => {

  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState(null);
  const [description, setDescription] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [errors, setErrors] = useState({
    customer_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    country: "",
    avatar: "",
  });
  const hasFetched = useRef(false);
  const modalRef = useRef(null);

  const options2 = useMemo(() => [
    { value: "", label: "Choose Country" },
    { value: "India", label: "India" },
    { value: "USA", label: "USA" },
    { value: "Germany", label: "Germany" },
    { value: "Mexico", label: "Mexico" },
  ], []);

  const resetForm = useCallback(() => {
    setCustomerName("");
    setEmail("");
    setPhone("");
    setAddress("");
    setCity("");
    setCountry(null);
    setDescription("");
    setAvatar(null);
    setErrors({});
    hasFetched.current = false;
  }, []);

  const validateField = useCallback((name, value) => {
    switch (name) {
      case "customer_name":
        return value.trim() ? "" : "Customer Name is required";
      case "email":
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "" : "Valid email is required";
      case "phone":
        return value.trim() ? "" : "Phone is required";
      case "address":
        return value.trim() ? "" : "Address is required";
      case "city":
        return value ? "" : "City is required";
      case "country":
        return value ? "" : "Country is required";
      default:
        return "";
    }
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {
      customer_name: validateField("customer_name", customerName),
      email: validateField("email", email),
      phone: validateField("phone", phone),
      address: validateField("address", address),
      city: validateField("city", city),
      country: validateField("country", country ? country.value : ""),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  }, [customerName, email, phone, address, city, country]);

  useEffect(() => {
    if (customer?.id && !hasFetched.current) {
      setLoading(true);
      hasFetched.current = true;
      apiClient.post("/edit-customer", { id: customer.id })
        .then(async (response) => {
          const data = await response;
          if (data.success && data.data) {
            setCustomerName(data.data.customer_name || "");
            setEmail(data.data.email || "");
            setPhone(data.data.phone || "");
            setAddress(data.data.address || "");
            setCity(data.data.city || "");
            setCountry(options2.find((opt) => opt.value === data.data.country) || null);
            setDescription(data.data.description || "");
            setAvatar(null);
          } else {
            showToast(data.message || "Invalid customer data", "error", "Customers");
          }
        })
        .catch((error) => {
          showToast(error.message || "Failed to fetch customer", "error", "Customers");
        })
        .finally(() => setLoading(false));
    } else if (!customer?.id) {
      resetForm();
      hasFetched.current = false;
    }
  }, [customer?.id, showToast, resetForm, options2]);

  useEffect(() => {
    modalRef.current = document.getElementById("customer-modal");
    const handleModalClose = () => {
      resetForm();
      hasFetched.current = false;
      dispatch(setCurrentCustomer(null));
      onClose(); // Notify parent on modal close
    };
    if (modalRef.current) {
      modalRef.current.addEventListener("hidden.bs.modal", handleModalClose);
      return () => modalRef.current.removeEventListener("hidden.bs.modal", handleModalClose);
    }
  }, [resetForm, onClose, dispatch]);
  
  // Handle show/hide based on show prop
  useEffect(() => {
    if (!modalRef.current) return;
    
    // Import Bootstrap's Modal functionality
    const bootstrap = window.bootstrap || {};
    if (!bootstrap.Modal) {
      // If Bootstrap JS is not loaded, try to load it
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/bootstrap@5.0.2/dist/js/bootstrap.bundle.min.js';
      document.body.appendChild(script);
      return;
    }
    
    const bsModal = bootstrap.Modal.getInstance(modalRef.current) || 
                   new bootstrap.Modal(modalRef.current, { backdrop: 'static' });
                   
    if (show) {
      bsModal.show();
    } else {
      bsModal.hide();
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append("customer_name", customerName);
        formData.append("email", email);
        formData.append("phone", phone);
        formData.append("address", address);
        formData.append("city", city);
        formData.append("country", country ? country.value : "");
        formData.append("description", description);
        formData.append("business_id", globals.businessId);
        if (avatar) {
          formData.append("avatar", avatar); // New file uploaded
        } else if (customer?.id && customer?.avatar) {
          formData.append("avatar", customer.avatar); // Existing image path
        }
        let response;
        if (customer?.id) {
          formData.append("id", customer.id);
          response = await apiClient.post("/update-customer", formData, true);
        } else {
          response = await apiClient.post("/store-customer", formData, true);
        }
        // console.log("Customer response:", response);
        if (response.success) {
          showToast(
            customer?.id ? "Customer updated successfully" : "Customer created successfully",
            "success",
            "Customers"
          );
          resetForm();
          onOperationComplete(response); // Pass response to parent
          document.getElementById("customer-modal").querySelector('[data-bs-dismiss="modal"]').click();
        } else {
          showToast(response.message || "Failed to save customer", "error", "Customers");
        }
      } catch (error) {
        showToast(error.message || "Failed to save customer", "error", "Customers");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div>
      <div className="modal fade" id="customer-modal">
        <div className="modal-dialog modal-dialog-centered custom-modal-two">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header">
                  <div className="page-title">
                    <h4>{customer ? "Edit Customer" : "Add Customer"}</h4>
                  </div>
                  <button type="button" className="close" data-bs-dismiss="modal" aria-label="Close">
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body custom-modal-body">
                  <form onSubmit={handleSubmit} className={loading ? "form-disabled" : ""}>
                    <div className="row">
                      <div className="col-lg-12">
                        <div className="new-employee-field">
                          <span>Avatar</span>
                          <div className="profile-pic-upload mb-2">
                            <div className="profile-pic">
                              {avatar || customer?.avatar ? (
                                <ImageWithBasePath
                                  src={avatar ? URL.createObjectURL(avatar) : customer?.avatar}
                                  alt="Customer"
                                  className="profile-preview"
                                />
                              ) : (
                                <span>Profile Photo</span>
                              )}
                            </div>
                            <div className="input-blocks mb-0">
                              <div className="image-upload mb-0">
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      if (!["image/jpeg", "image/png"].includes(file.type)) {
                                        setErrors((prev) => ({
                                          ...prev,
                                          avatar: "Only JPEG or PNG files are allowed",
                                        }));
                                        return;
                                      }
                                      if (file.size > 2 * 1024 * 1024) {
                                        setErrors((prev) => ({
                                          ...prev,
                                          avatar: "Image size must be less than 2MB",
                                        }));
                                        return;
                                      }
                                      setAvatar(file);
                                      setErrors((prev) => ({ ...prev, avatar: "" }));
                                    }
                                  }}
                                />
                                <div className="image-uploads">
                                  <h4>Change Image</h4>
                                </div>
                              </div>
                              {errors.avatar && <div className="invalid-feedback">{errors.avatar}</div>}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-4">
                        <div className="input-blocks">
                          <label>Customer Name</label>
                          <input
                            type="text"
                            name="customer_name"
                            value={customerName}
                            onChange={(e) => {
                              setCustomerName(e.target.value);
                              setErrors((prev) => ({
                                ...prev,
                                customer_name: validateField("customer_name", e.target.value),
                              }));
                            }}
                            className={`form-control ${errors.customer_name ? "is-invalid" : ""}`}
                          />
                          {errors.customer_name && <div className="invalid-feedback">{errors.customer_name}</div>}
                        </div>
                      </div>
                      <div className="col-lg-4">
                        <div className="input-blocks">
                          <label>Email</label>
                          <input
                            type="email"
                            name="email"
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              setErrors((prev) => ({
                                ...prev,
                                email: validateField("email", e.target.value),
                              }));
                            }}
                            className={`form-control ${errors.email ? "is-invalid" : ""}`}
                          />
                          {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                        </div>
                      </div>
                      <div className="col-lg-4">
                        <div className="input-blocks">
                          <label>Phone</label>
                          <input
                            type="text"
                            name="phone"
                            value={phone}
                            onChange={(e) => {
                              setPhone(e.target.value);
                              setErrors((prev) => ({
                                ...prev,
                                phone: validateField("phone", e.target.value),
                              }));
                            }}
                            className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                          />
                          {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="input-blocks">
                          <label>Address</label>
                          <input
                            type="text"
                            name="address"
                            value={address}
                            onChange={(e) => {
                              setAddress(e.target.value);
                              setErrors((prev) => ({
                                ...prev,
                                address: validateField("address", e.target.value),
                              }));
                            }}
                            className={`form-control ${errors.address ? "is-invalid" : ""}`}
                          />
                          {errors.address && <div className="invalid-feedback">{errors.address}</div>}
                        </div>
                      </div>
                      <div className="col-lg-6 col-sm-10 col-10">
                        <div className="input-blocks">
                          <label>City</label>
                          <input
                            type="text"
                            name="city"
                            value={city}
                            onChange={(e) => {
                              setCity(e.target.value);
                              setErrors((prev) => ({
                                ...prev,
                                city: validateField("city", e.target.value),
                              }));
                            }}
                            className={`form-control ${errors.city ? "is-invalid" : ""}`}
                          />
                          {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                        </div>
                      </div>
                      <div className="col-lg-6 col-sm-10 col-10">
                        <div className="input-blocks">
                          <label>Country</label>
                          <Select
                            className={`select ${errors.country ? "is-invalid" : ""}`}
                            options={options2}
                            value={country}
                            onChange={(opt) => {
                              setCountry(opt);
                              setErrors((prev) => ({
                                ...prev,
                                country: validateField("country", opt ? opt.value : ""),
                              }));
                            }}
                          />
                          {errors.country && <div className="invalid-feedback">{errors.country}</div>}
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="mb-0 input-blocks">
                          <label className="form-label">Description</label>
                          <textarea
                            name="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="form-control mb-1"
                            maxLength={600}
                          />
                          <p>Maximum 600 Characters</p>
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer-btn">
                      <button type="button" className="btn btn-cancel me-2" data-bs-dismiss="modal">
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-submit" disabled={loading}>
                        {loading ? "Processing..." : (customer ? "Update Customer" : "Create Customer")}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CustomerModal.propTypes = {
  customer: PropTypes.shape({
    id: PropTypes.number,
    customer_name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    description: PropTypes.string,
    avatar: PropTypes.string,
  }),
  onOperationComplete: PropTypes.func,
  onClose: PropTypes.func,
  show: PropTypes.bool,
};

CustomerModal.defaultProps = {
  customer: null,
  onOperationComplete: () => { },
  onClose: () => { },
  show: false,
};

export default CustomerModal;